import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";

export function OnboardingSafety() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!ack) {
      setError("Please confirm before continuing.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/me/safety", { acknowledged: true });
      await refresh();
      navigate("/welcome");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      posterTitle="One page, read it properly."
      posterBody="Infusions and blending: yes. Stills: not here. It matters more in some places than others."
      step={4}
    >
      <span className="eyebrow">Step 4 of 4 · the only page like this</span>
      <h2 style={{ margin: "12px 0 0", fontSize: 38, lineHeight: 1.05, letterSpacing: "-0.028em", marginLeft: "-0.055em" }}>
        Where the line is
      </h2>
      <p style={{ margin: "16px 0 26px", fontSize: 15, lineHeight: 1.65 }}>
        Steeping fruit, roots and spices in spirit you bought is legal almost everywhere, and it's what this whole site
        is for. Making the spirit yourself is a different thing with different laws.
      </p>

      <div style={{ borderTop: "2px solid var(--color-divider)" }}>
        <Row dot="var(--color-accent)" title="What's here">
          Macerations, infusions, oleo saccharum, fortifying, sweetening and blending — all built on purchased base
          spirit.
        </Row>
        <Row dot="var(--color-neutral-500)" title="What isn't">
          Still designs, wash-to-spirit procedures, cut schedules. We don't host them and we remove them.
        </Row>
        <Row dot="var(--color-neutral-500)" title="Two real cautions" last>
          Some botanicals are toxic in quantity — weigh them, and heed the red flags we put on recipes. Anything you
          bottle for someone else needs a label with the strength on it.
        </Row>
      </div>

      <label className="radio" style={{ alignItems: "flex-start", margin: "24px 0" }}>
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span className="dot" style={{ marginTop: 2 }} />
        <span style={{ fontSize: 13.5, lineHeight: 1.55 }}>Understood. I'll check what applies where I live.</span>
      </label>
      {error && <p className="error-text">{error}</p>}

      <div className="flex-row">
        <button className="btn btn-primary" style={{ minHeight: 46 }} disabled={busy} onClick={submit}>
          Take me in
        </button>
      </div>
    </AuthLayout>
  );
}

function Row({ dot, title, children, last }: { dot: string; title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "24px minmax(0,1fr)",
        gap: 14,
        padding: "16px 0",
        borderBottom: last ? "2px solid var(--color-divider)" : "1px solid var(--color-divider)",
      }}
    >
      <span style={{ width: 10, height: 10, background: dot, marginTop: 6 }} />
      <span>
        <span style={{ display: "block", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 15 }}>{title}</span>
        <span className="muted" style={{ display: "block", marginTop: 5, fontSize: 13.5, lineHeight: 1.6 }}>
          {children}
        </span>
      </span>
    </div>
  );
}
